import React, { useState, useMemo, useEffect, useCallback } from 'react';
import Swal from 'sweetalert2';
import './MyCasesCalendar.css';
import ExternalLawyers from './ExternalLawyers';
import Transactions from './Transactions';
import Attachments from './Attachments';
import CaseDetails from './CaseDetails';

const MyCasesCalendar = ({ onBack, token }) => {
  const [currentDate, setCurrentDate] = useState(new Date()); // Current month/year
  const [selectedDate, setSelectedDate] = useState(new Date()); // Today
  const [viewMode, setViewMode] = useState('2weeks'); // '2weeks', '1week', '1month'
  const [searchTerm, setSearchTerm] = useState('');
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [deleting, setDeleting] = useState(null);
  const [showProceedingModal, setShowProceedingModal] = useState(false);
  const [selectedCaseForProceeding, setSelectedCaseForProceeding] = useState(null);
  const [proceedingForm, setProceedingForm] = useState({ note: '', date: '', time: '', judge_name: '' });
  const [proceedingLoading, setProceedingLoading] = useState(false);
  const [closingCase, setClosingCase] = useState(null);
  const [showCloseCaseModal, setShowCloseCaseModal] = useState(false);
  const [selectedCaseForClosing, setSelectedCaseForClosing] = useState(null);
  const [closeNoteForm, setCloseNoteForm] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [openMenuId, setOpenMenuId] = useState(null);
  
  // Add Lawyer modal states
  const [showAddLawyerModal, setShowAddLawyerModal] = useState(false);
  const [selectedCaseForLawyer, setSelectedCaseForLawyer] = useState(null);
  const [lawyers, setLawyers] = useState([]);
  const [lawyerSearchTerm, setLawyerSearchTerm] = useState('');
  const [loadingLawyers, setLoadingLawyers] = useState(false);
  const [selectedLawyers, setSelectedLawyers] = useState([]);
  const [assigningLawyer, setAssigningLawyer] = useState(false);
  
  // External Lawyers view state
  const [showExternalLawyers, setShowExternalLawyers] = useState(false);
  const [selectedCaseForExternal, setSelectedCaseForExternal] = useState(null);
  
  // Transactions view state
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedCaseForTransactions, setSelectedCaseForTransactions] = useState(null);
  
  // Attachments view state
  const [showAttachments, setShowAttachments] = useState(false);
  const [selectedCaseForAttachments, setSelectedCaseForAttachments] = useState(null);

  // Case Details view state
  const [showCaseDetails, setShowCaseDetails] = useState(false);
  const [selectedCaseForDetails, setSelectedCaseForDetails] = useState(null);

  // Fetch cases from API
  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/get/case`;
      console.log('Fetching cases from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      console.log('API Response status:', response.status);

      const data = await response.json();
      console.log('API Response:', data);

      if (response.ok && data.data && Array.isArray(data.data)) {
        // Transform API data to match our case structure
        const transformedCases = data.data.map(apiCase => {
          // Use institution_date instead of created_at for calendar display
          const institutionDate = new Date(apiCase.institution_date);
          const dateStr = `${institutionDate.getFullYear()}-${String(institutionDate.getMonth() + 1).padStart(2, '0')}-${String(institutionDate.getDate()).padStart(2, '0')}`;
          
          return {
            id: apiCase.id,
            date: dateStr,
            caseNumber: apiCase.case_number,
            title: `Case #${apiCase.case_number}`,
            refName: `${apiCase.party_a} vs ${apiCase.party_b}`,
            partyA: apiCase.party_a,
            partyB: apiCase.party_b,
            caseType: apiCase.case_type,
            description: apiCase.description,
            clientName: apiCase.client_name,
            clientPhone: apiCase.client_phone,
            courtName: apiCase.court_name,
            judgeName: apiCase.judge_name,
            caseLocation: apiCase.case_location,
            institutionDate: apiCase.institution_date,
            status: apiCase.close_status === 1 ? 'Closed' : 'Active',
            createdAt: apiCase.created_at,
            proceedings: apiCase.proceedings || [],
          };
        });

        setCases(transformedCases);
        console.log('Transformed cases:', transformedCases);
      } else {
        setError(data.message || 'Failed to fetch cases');
      }
    } catch (err) {
      console.error('Error fetching cases:', err);
      setError(err.message || 'Failed to fetch cases');
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch cases on component mount or when refreshKey changes
  useEffect(() => {
    fetchCases();
  }, [token, refreshKey, fetchCases]);

  // Handle case deletion
  const handleDeleteCase = async (caseId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You want to delete this case?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (!result.isConfirmed) {
      return;
    }

    try {
      setDeleting(caseId);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/delete/case/${caseId}`;
      console.log('Deleting case from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      console.log('Delete response status:', response.status);

      const data = await response.json();
      console.log('Delete response:', data);

      if (response.ok) {
        // Remove the deleted case from the list
        setCases(cases.filter(c => c.id !== caseId));
        console.log('Case deleted successfully');
        Swal.fire({
          title: 'Deleted!',
          text: 'Case has been deleted successfully.',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to delete case',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error deleting case:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to delete case',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setDeleting(null);
    }
  };

  // Toggle menu visibility
  const toggleMenu = (caseId) => {
    setOpenMenuId(openMenuId === caseId ? null : caseId);
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openMenuId && !event.target.closest('.case-menu-container')) {
        setOpenMenuId(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [openMenuId]);

  // Fetch all lawyers
  const fetchLawyers = async () => {
    try {
      setLoadingLawyers(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/guests/getLawyers`;
      console.log('Fetching lawyers from:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
      });

      const data = await response.json();
      console.log('Lawyers API Response:', data);
      console.log('First lawyer object:', data.data && data.data[0]);

      if (response.ok && data.data && Array.isArray(data.data)) {
        setLawyers(data.data);
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to fetch lawyers',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error fetching lawyers:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to fetch lawyers',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setLoadingLawyers(false);
    }
  };

  // Assign lawyers to case
  const handleAssignLawyers = async () => {
    if (selectedLawyers.length === 0) {
      Swal.fire({
        title: 'Warning',
        text: 'Please select at least one lawyer',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setAssigningLawyer(true);
      
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/cases/assign-lawyers/${selectedCaseForLawyer.id}`;
      console.log('Assigning lawyers to case:', apiUrl);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          lawyer_ids: selectedLawyers
        }),
      });

      const data = await response.json();
      console.log('Assign lawyers response:', data);

      if (response.ok) {
        Swal.fire({
          title: 'Success!',
          text: 'Lawyers assigned successfully',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
        setShowAddLawyerModal(false);
        setSelectedLawyers([]);
        setLawyerSearchTerm('');
        setSelectedCaseForLawyer(null);
      } else {
        Swal.fire({
          title: 'Error!',
          text: data.message || 'Failed to assign lawyers',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error assigning lawyers:', err);
      Swal.fire({
        title: 'Error!',
        text: err.message || 'Failed to assign lawyers',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setAssigningLawyer(false);
    }
  };

  // Open Add Lawyer modal
  const openAddLawyerModal = (caseItem) => {
    setSelectedCaseForLawyer(caseItem);
    setShowAddLawyerModal(true);
    setSelectedLawyers([]);
    setLawyerSearchTerm('');
    fetchLawyers();
  };

  // Toggle lawyer selection
  const toggleLawyerSelection = (lawyerId) => {
    setSelectedLawyers(prev => {
      if (prev.includes(lawyerId)) {
        return prev.filter(id => id !== lawyerId);
      } else {
        return [...prev, lawyerId];
      }
    });
  };

  // Handle add proceeding
  const handleAddProceeding = async (caseId) => {
    if (!proceedingForm.note || !proceedingForm.date || !proceedingForm.time) {
      Swal.fire({
        title: 'Warning',
        text: 'Please fill in all fields',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setProceedingLoading(true);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/add/proceeding`;
      const datetime = `${proceedingForm.date} ${proceedingForm.time}`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({
          case_id: caseId,
          note: proceedingForm.note,
          datetime: datetime,
          judge_name: proceedingForm.judge_name || null,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the entire calendar to get updated proceedings
        setShowProceedingModal(false);
        setProceedingForm({ note: '', date: '', time: '', judge_name: '' });
        setSelectedCaseForProceeding(null);
        setRefreshKey(prev => prev + 1); // Trigger calendar refresh
        Swal.fire({
          title: 'Success!',
          text: 'Proceeding added successfully!',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: data.message || 'Failed to add proceeding',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error adding proceeding:', err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to add proceeding',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setProceedingLoading(false);
    }
  };

  // Handle close case
  const handleCloseCase = async (caseId, note) => {
    if (!note || note.trim() === '') {
      Swal.fire({
        title: 'Warning',
        text: 'Please enter a closing note',
        icon: 'warning',
        confirmButtonColor: '#3085d6'
      });
      return;
    }

    try {
      setClosingCase(caseId);
      const apiUrl = `${process.env.REACT_APP_API_URL}/api/lawyer/close/case`;

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
        },
        body: JSON.stringify({ case_id: caseId, note: note }),
      });

      const data = await response.json();

      if (response.ok) {
        // Refresh the calendar to get updated case status
        setShowCloseCaseModal(false);
        setCloseNoteForm('');
        setSelectedCaseForClosing(null);
        setRefreshKey(prev => prev + 1); // Trigger calendar refresh
        Swal.fire({
          title: 'Success!',
          text: 'Case closed successfully!',
          icon: 'success',
          confirmButtonColor: '#3085d6'
        });
      } else {
        Swal.fire({
          title: 'Error',
          text: data.message || 'Failed to close case',
          icon: 'error',
          confirmButtonColor: '#3085d6'
        });
      }
    } catch (err) {
      console.error('Error closing case:', err);
      Swal.fire({
        title: 'Error',
        text: err.message || 'Failed to close case',
        icon: 'error',
        confirmButtonColor: '#3085d6'
      });
    } finally {
      setClosingCase(null);
    }
  };

  // Get the number of days in a month
  const getDaysInMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  // Get the first day of the month (0 = Monday, 6 = Sunday for Monday-start calendar)
  const getFirstDayOfMonth = (date) => {
    const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
    // Convert from Sunday=0 to Monday=0 format
    return day === 0 ? 6 : day - 1;
  };

  // Get all cases for a specific date
  const getCasesForDate = useCallback((date) => {
    const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    return cases.filter(c => c.date === dateStr);
  }, [cases]);

  // Get cases count for each date (only active cases, respecting search filter)
  const getCasesCountForDate = useCallback((date) => {
    let filtered = getCasesForDate(date).filter(c => c.status === 'Active');
    
    // Apply search filter if present
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        // Search by Case Number
        (c.caseNumber && c.caseNumber.toString().toLowerCase().includes(search)) ||
        // Search by Reference Name (party names)
        (c.refName && c.refName.toLowerCase().includes(search)) ||
        (c.partyA && c.partyA.toLowerCase().includes(search)) ||
        (c.partyB && c.partyB.toLowerCase().includes(search)) ||
        // Search by Case Title (case type)
        (c.caseType && c.caseType.toLowerCase().includes(search)) ||
        (c.title && c.title.toLowerCase().includes(search)) ||
        // Search by Phone Number
        (c.clientPhone && c.clientPhone.toLowerCase().includes(search))
      );
    }
    
    return filtered.length;
  }, [getCasesForDate, searchTerm]);

  // Handle previous month
  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  // Handle next month
  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  // Handle date click
  const handleDateClick = (day) => {
    setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), day));
  };

  // Get selected date's cases
  const selectedCases = useMemo(() => {
    let filtered = getCasesForDate(selectedDate);
    // Only show active cases (exclude closed cases)
    filtered = filtered.filter(c => c.status === 'Active');
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        // Search by Case Number
        (c.caseNumber && c.caseNumber.toString().toLowerCase().includes(search)) ||
        // Search by Reference Name (party names)
        (c.refName && c.refName.toLowerCase().includes(search)) ||
        (c.partyA && c.partyA.toLowerCase().includes(search)) ||
        (c.partyB && c.partyB.toLowerCase().includes(search)) ||
        // Search by Case Title (case type)
        (c.caseType && c.caseType.toLowerCase().includes(search)) ||
        (c.title && c.title.toLowerCase().includes(search)) ||
        // Search by Phone Number
        (c.clientPhone && c.clientPhone.toLowerCase().includes(search))
      );
    }
    return filtered;
  }, [selectedDate, searchTerm, getCasesForDate]);

  // Render calendar grid
  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    // Empty cells for days before month starts
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day empty"></div>
      );
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
      const casesCount = getCasesCountForDate(date);
      const isSelected = 
        selectedDate.getDate() === day &&
        selectedDate.getMonth() === currentDate.getMonth() &&
        selectedDate.getFullYear() === currentDate.getFullYear();

      days.push(
        <div
          key={day}
          className={`calendar-day ${isSelected ? 'selected' : ''}`}
          onClick={() => handleDateClick(day)}
        >
          <span className="day-number">{day}</span>
          {casesCount > 0 && (
            <span className="cases-badge">{casesCount}</span>
          )}
        </div>
      );
    }

    return days;
  };

  const monthName = currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });

  // Show Attachments view if selected
  if (showAttachments && selectedCaseForAttachments) {
    return (
      <Attachments
        caseData={selectedCaseForAttachments}
        onBack={() => {
          setShowAttachments(false);
          setSelectedCaseForAttachments(null);
        }}
        token={token}
      />
    );
  }

  // Show Transactions view if selected
  if (showTransactions && selectedCaseForTransactions) {
    return (
      <Transactions
        caseData={selectedCaseForTransactions}
        onBack={() => {
          setShowTransactions(false);
          setSelectedCaseForTransactions(null);
        }}
        token={token}
      />
    );
  }

  // Show External Lawyers view if selected
  if (showExternalLawyers && selectedCaseForExternal) {
    return (
      <ExternalLawyers
        caseData={selectedCaseForExternal}
        onBack={() => {
          setShowExternalLawyers(false);
          setSelectedCaseForExternal(null);
        }}
        token={token}
      />
    );
  }

  // Show Case Details view if selected
  if (showCaseDetails && selectedCaseForDetails) {
    return (
      <CaseDetails
        caseData={selectedCaseForDetails}
        onBack={() => {
          setShowCaseDetails(false);
          setSelectedCaseForDetails(null);
        }}
      />
    );
  }

  return (
    <div className="my-cases-container">
      {/* Header */}
      <div className="cases-header">
        <button className="back-button" onClick={onBack}>
          ←
        </button>
        <h2>My Cases</h2>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <span className="search-icon">🔍</span>
        <input
          type="text"
          placeholder="Search by Case Number, Reference Name, Case Title, Phone Number"
          className="search-input"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="cases-content">
        {/* Calendar Section */}
        <div className="calendar-section">
          {/* Month Navigation */}
          <div className="month-navigation">
            <button className="nav-button" onClick={handlePrevMonth}>
              ←
            </button>
            <span className="month-label">{monthName}</span>
            <button className="nav-button" onClick={handleNextMonth}>
              →
            </button>
          </div>

          {/* View Mode Toggle */}
          <div className="view-mode-toggle">
            <button 
              className={`mode-button ${viewMode === '2weeks' ? 'active' : ''}`}
              onClick={() => setViewMode('2weeks')}
            >
              2 weeks
            </button>
            <button 
              className={`mode-button ${viewMode === '1week' ? 'active' : ''}`}
              onClick={() => setViewMode('1week')}
            >
              1 week
            </button>
            <button 
              className={`mode-button ${viewMode === '1month' ? 'active' : ''}`}
              onClick={() => setViewMode('1month')}
            >
              1 month
            </button>
          </div>

          {/* Day Headers */}
          <div className="calendar-header">
            <div className="day-header">MON</div>
            <div className="day-header">TUE</div>
            <div className="day-header">WED</div>
            <div className="day-header">THU</div>
            <div className="day-header">FRI</div>
            <div className="day-header">SAT</div>
            <div className="day-header">SUN</div>
          </div>

          {/* Calendar Grid */}
          <div className="calendar-grid">
            {renderCalendar()}
          </div>
        </div>

        {/* Cases List Section */}
        <div className="cases-list-section">
          <div className="selected-date-info">
            <h3>
              {selectedDate.toLocaleDateString('default', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
                year: 'numeric'
              })}
            </h3>
            <span className="case-count">
              {selectedCases.length} {selectedCases.length === 1 ? 'case' : 'cases'}
            </span>
          </div>

          {loading ? (
            <div className="loading-state">
              <p>Loading cases...</p>
            </div>
          ) : error ? (
            <div className="error-state">
              <p>{error}</p>
            </div>
          ) : selectedCases.length > 0 ? (
            <div className="cases-list">
              {selectedCases.map(caseItem => {
                // Get latest proceeding for hearing time if available
                const latestProceeding = caseItem.proceedings && caseItem.proceedings.length > 0 
                  ? caseItem.proceedings[caseItem.proceedings.length - 1] 
                  : null;
                const hearingDate = latestProceeding 
                  ? new Date(latestProceeding.datetime) 
                  : new Date(caseItem.institutionDate);
                const hearingTime = latestProceeding 
                  ? hearingDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
                  : '6:39 PM';
                const displayDate = hearingDate.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

                return (
                <div key={caseItem.id} className="case-item" onClick={() => {
                  setSelectedCaseForDetails(caseItem);
                  setShowCaseDetails(true);
                }} style={{ cursor: 'pointer' }}>
                  <div className="case-header">
                    <div className="case-title-section">
                      <div className="case-time-date">
                        <span className="hearing-time">Hearing Time: {hearingTime}</span>
                        <span className="hearing-date">Date: {displayDate}</span>
                      </div>
                      <div className="case-meta">
                        <span className="case-number">Case No.: {caseItem.caseNumber}</span>
                        <span className="case-title-text">Case Title: {caseItem.caseType}</span>
                      </div>
                    </div>
                    
                    {/* Three-dot menu */}
                    <div className="case-menu-container" onClick={(e) => e.stopPropagation()}>
                      <button
                        className="menu-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleMenu(caseItem.id);
                        }}
                        aria-label="More options"
                      >
                        ⋮
                      </button>
                      
                      {openMenuId === caseItem.id && (
                        <div className="case-menu-dropdown">
                          <button className="menu-item" onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            setSelectedCaseForExternal(caseItem);
                            setShowExternalLawyers(true);
                          }}>
                            <span className="menu-icon">👤+</span>
                            <span className="menu-text">Add Lawyer</span>
                          </button>
                          
                          <button className="menu-item" onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            console.log('Opening Transactions for case:', caseItem);
                            setSelectedCaseForTransactions(caseItem);
                            setShowTransactions(true);
                          }}>
                            <span className="menu-icon">💵</span>
                            <span className="menu-text">Transactions</span>
                          </button>
                          
                          <button className="menu-item" onClick={(e) => {
                            e.stopPropagation();
                            setOpenMenuId(null);
                            console.log('Opening Attachments for case:', caseItem);
                            setSelectedCaseForAttachments(caseItem);
                            setShowAttachments(true);
                          }}>
                            <span className="menu-icon">📎</span>
                            <span className="menu-text">Attachments</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="case-parties">
                    <p className="party-info">{caseItem.partyA}</p>
                    <h3>VS</h3>
                    <p className="party-info">{caseItem.partyB}</p>
                  </div>

                  {caseItem.judgeName && (
                    <div className="case-judge-info">
                      <span className="judge-icon">⚖️</span>
                      <span className="judge-label">Judge:</span>
                      <span className="judge-name">{caseItem.judgeName}</span>
                    </div>
                  )}

                  <div className="case-reference">
                    <span className="reference-icon">ⓘ</span>
                    <span className="reference-label">With reference of:</span>
                    <span className="reference-value">Respondent/Defendant</span>
                  </div>

                  {caseItem.description && (
                    <div className="case-description">
                      <span className="description-icon">ⓘ</span>
                      <span className="description-label">Description:</span>
                      <p className="description-text">{caseItem.description}</p>
                    </div>
                  )}

                  <div className="case-details" style={{ display: 'none' }}>
                    {caseItem.clientName && (
                      <div className="detail-row">
                        <span className="detail-label">Client:</span>
                        <span className="detail-value">{caseItem.clientName}</span>
                      </div>
                    )}
                    {caseItem.clientPhone && (
                      <div className="detail-row">
                        <span className="detail-label">Phone:</span>
                        <span className="detail-value">{caseItem.clientPhone}</span>
                      </div>
                    )}
                    {caseItem.courtName && (
                      <div className="detail-row">
                        <span className="detail-label">Court:</span>
                        <span className="detail-value">{caseItem.courtName}</span>
                      </div>
                    )}
                    {caseItem.judgeName && (
                      <div className="detail-row">
                        <span className="detail-label">Judge:</span>
                        <span className="detail-value">{caseItem.judgeName}</span>
                      </div>
                    )}
                    {caseItem.caseLocation && (
                      <div className="detail-row">
                        <span className="detail-label">Location:</span>
                        <span className="detail-value">{caseItem.caseLocation}</span>
                      </div>
                    )}
                  </div>

                  {caseItem.proceedings && caseItem.proceedings.length > 0 && (
                    <div className="case-proceedings">
                      <span className="proceedings-label">Proceedings ({caseItem.proceedings.length})</span>
                      <div className="proceedings-list">
                        {caseItem.proceedings.map((proceeding, idx) => (
                          <div key={idx} className="proceeding-item">
                            <span className="proceeding-date">
                              {new Date(proceeding.datetime).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} {new Date(proceeding.datetime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                            </span>
                            <span className="proceeding-note">{proceeding.note}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="case-actions" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="action-button plus-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCaseForProceeding(caseItem);
                        setShowProceedingModal(true);
                      }}
                      title="Add a proceeding"
                    >
                      <span className="plus-icon">+</span>
                    </button>
                    <button
                      className="action-button close-button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCaseForClosing(caseItem);
                        setShowCloseCaseModal(true);
                        setCloseNoteForm('');
                      }}
                      disabled={caseItem.status === 'Closed'}
                    >
                      {caseItem.status === 'Closed' ? '✓ Closed' : 'Close Case'}
                    </button>
                  </div>
                </div>
                );
              })}
            </div>
          ) : (
            <div className="no-cases">
              <p>No cases found for this date</p>
              {searchTerm && (
                <button 
                  className="clear-search-button"
                  onClick={() => setSearchTerm('')}
                >
                  Clear Search
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Proceeding Modal */}
      {showProceedingModal && selectedCaseForProceeding && (
        <div className="modal-overlay" onClick={() => setShowProceedingModal(false)}>
          <div className="modal-content proceeding-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Proceeding</h3>
              <button
                className="modal-close"
                onClick={() => setShowProceedingModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="case-title">Case: {selectedCaseForProceeding.title}</p>

              <div className="form-group">
                <label>Proceeding Note *</label>
                <textarea
                  value={proceedingForm.note}
                  onChange={(e) => setProceedingForm({ ...proceedingForm, note: e.target.value })}
                  placeholder="Enter proceeding details"
                  rows="4"
                />
              </div>

              <div className="form-group">
                <label>Judge Name</label>
                <input
                  type="text"
                  value={proceedingForm.judge_name}
                  onChange={(e) => setProceedingForm({ ...proceedingForm, judge_name: e.target.value })}
                  placeholder="Enter judge's name (optional)"
                />
              </div>

              <div className="date-time-group">
                <div className="form-group">
                  <label>Date *</label>
                  <input
                    type="date"
                    value={proceedingForm.date}
                    onChange={(e) => setProceedingForm({ ...proceedingForm, date: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label>Time *</label>
                  <input
                    type="time"
                    value={proceedingForm.time}
                    onChange={(e) => setProceedingForm({ ...proceedingForm, time: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowProceedingModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={() => handleAddProceeding(selectedCaseForProceeding.id)}
                disabled={proceedingLoading}
              >
                {proceedingLoading ? 'Adding...' : 'Add Proceeding'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Close Case Modal */}
      {showCloseCaseModal && selectedCaseForClosing && (
        <div className="modal-overlay" onClick={() => setShowCloseCaseModal(false)}>
          <div className="modal-content close-case-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Close Case</h3>
              <button
                className="modal-close"
                onClick={() => setShowCloseCaseModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="case-title">Case: {selectedCaseForClosing.title}</p>
              <p className="warning-text">⚠️ This action will close the case. Please provide a closing note.</p>

              <div className="form-group">
                <label>Closing Note *</label>
                <textarea
                  value={closeNoteForm}
                  onChange={(e) => setCloseNoteForm(e.target.value)}
                  placeholder="Enter closing details or summary"
                  rows="4"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowCloseCaseModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button close-confirm-button"
                onClick={() => handleCloseCase(selectedCaseForClosing.id, closeNoteForm)}
                disabled={closingCase === selectedCaseForClosing.id || !closeNoteForm.trim()}
              >
                {closingCase === selectedCaseForClosing.id ? 'Closing...' : 'Close Case'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Lawyer Modal */}
      {showAddLawyerModal && selectedCaseForLawyer && (
        <div className="modal-overlay" onClick={() => setShowAddLawyerModal(false)}>
          <div className="modal-content add-lawyer-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add Lawyer to Case</h3>
              <button
                className="modal-close"
                onClick={() => setShowAddLawyerModal(false)}
              >
                ✕
              </button>
            </div>

            <div className="modal-body">
              <p className="case-title">Case #{selectedCaseForLawyer.caseNumber}: {selectedCaseForLawyer.caseType}</p>

              {/* Search Input */}
              <div className="lawyer-search-container">
                <input
                  type="text"
                  className="lawyer-search-input"
                  placeholder="Search by name or phone number..."
                  value={lawyerSearchTerm}
                  onChange={(e) => setLawyerSearchTerm(e.target.value)}
                />
              </div>

              {/* Lawyers List */}
              <div className="lawyers-list-container">
                {loadingLawyers ? (
                  <div className="loading-state">
                    <p>Loading lawyers...</p>
                  </div>
                ) : (
                  <div className="lawyers-list">
                    {lawyers
                      .filter(lawyer => {
                        const searchLower = lawyerSearchTerm.toLowerCase();
                        // Handle different possible field names from API
                        const firstName = (lawyer.first_name || '')?.toLowerCase();
                        const lastName = (lawyer.last_name || '')?.toLowerCase();
                        const fullName = `${firstName} ${lastName}`.trim();
                        const name = (lawyer.name || lawyer.full_name || lawyer.user_name || '')?.toLowerCase();
                        const phone = (lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || '')?.toLowerCase();
                        
                        return firstName.includes(searchLower) || 
                               lastName.includes(searchLower) || 
                               fullName.includes(searchLower) ||
                               name.includes(searchLower) || 
                               phone.includes(searchLower);
                      })
                      .map(lawyer => {
                        // Handle different possible field names from API
                        const lawyerName = lawyer.name || 
                                          (lawyer.first_name && lawyer.last_name ? `${lawyer.first_name} ${lawyer.last_name}` : '') ||
                                          lawyer.first_name || 
                                          lawyer.last_name || 
                                          lawyer.full_name || 
                                          lawyer.user_name || 
                                          'Unknown';
                        const lawyerPhone = lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || 'No phone';
                        
                        return (
                        <div
                          key={lawyer.id}
                          className={`lawyer-item ${selectedLawyers.includes(lawyer.id) ? 'selected' : ''}`}
                          onClick={() => toggleLawyerSelection(lawyer.id)}
                        >
                          <div className="lawyer-info">
                            <div className="lawyer-avatar">
                              {lawyerName.charAt(0).toUpperCase()}
                            </div>
                            <div className="lawyer-details">
                              <div className="lawyer-name">{lawyerName}</div>
                              <div className="lawyer-phone">{lawyerPhone}</div>
                            </div>
                          </div>
                          <div className="lawyer-checkbox">
                            {selectedLawyers.includes(lawyer.id) ? '✓' : ''}
                          </div>
                        </div>
                        );
                      })}
                    {lawyers.filter(lawyer => {
                      const searchLower = lawyerSearchTerm.toLowerCase();
                      // Handle different possible field names from API
                      const firstName = (lawyer.first_name || '')?.toLowerCase();
                      const lastName = (lawyer.last_name || '')?.toLowerCase();
                      const fullName = `${firstName} ${lastName}`.trim();
                      const name = (lawyer.name || lawyer.full_name || lawyer.user_name || '')?.toLowerCase();
                      const phone = (lawyer.mobile_number || lawyer.phone || lawyer.phone_number || lawyer.mobile || '')?.toLowerCase();
                      
                      return firstName.includes(searchLower) || 
                             lastName.includes(searchLower) || 
                             fullName.includes(searchLower) ||
                             name.includes(searchLower) || 
                             phone.includes(searchLower);
                    }).length === 0 && (
                      <div className="no-lawyers">
                        <p>No lawyers found</p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {selectedLawyers.length > 0 && (
                <div className="selected-count">
                  {selectedLawyers.length} lawyer{selectedLawyers.length !== 1 ? 's' : ''} selected
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                className="cancel-button"
                onClick={() => setShowAddLawyerModal(false)}
              >
                Cancel
              </button>
              <button
                className="submit-button"
                onClick={handleAssignLawyers}
                disabled={assigningLawyer || selectedLawyers.length === 0}
              >
                {assigningLawyer ? 'Adding...' : `Add ${selectedLawyers.length > 0 ? selectedLawyers.length : ''} Lawyer${selectedLawyers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCasesCalendar;
